import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { calculateEligibility } from '../../services/calculatorService';

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const EligibilityCalculator = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      applicantMonthlyIncome: '',
      coApplicantIncome: '',
      existingEMIs: '',
      desiredLoanAmount: '',
      tenure: '',
      interestRate: '',
    },
  });

  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      setError('');
      const payload = {
        applicantMonthlyIncome: Number(formData.applicantMonthlyIncome),
        coApplicantIncome: Number(formData.coApplicantIncome) || 0,
        existingEMIs: Number(formData.existingEMIs) || 0,
        desiredLoanAmount: Number(formData.desiredLoanAmount),
        tenure: Number(formData.tenure),
        interestRate: Number(formData.interestRate),
      };
      const { data } = await calculateEligibility(payload);
      setResult(data.data || null);
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to calculate eligibility';
      setError(errMsg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Input Form */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Loan Eligibility Calculator
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter details to check pre-eligibility for an education loan.
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="applicantMonthlyIncome"
                  control={control}
                  rules={{ required: 'Monthly income is required', min: { value: 1, message: 'Must be greater than 0' } }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Applicant Monthly Income (₹)"
                      type="number"
                      size="small"
                      error={!!errors.applicantMonthlyIncome}
                      helperText={errors.applicantMonthlyIncome?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="coApplicantIncome"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Co-Applicant Monthly Income (₹)"
                      type="number"
                      size="small"
                      helperText="Optional - leave 0 if no co-applicant"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="existingEMIs"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Existing EMIs (₹)"
                      type="number"
                      size="small"
                      helperText="Total of all existing monthly EMIs"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="desiredLoanAmount"
                  control={control}
                  rules={{ required: 'Loan amount is required', min: { value: 1, message: 'Must be greater than 0' } }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Desired Loan Amount (₹)"
                      type="number"
                      size="small"
                      error={!!errors.desiredLoanAmount}
                      helperText={errors.desiredLoanAmount?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="tenure"
                  control={control}
                  rules={{ required: 'Tenure is required', min: { value: 1, message: 'Min 1 year' }, max: { value: 30, message: 'Max 30 years' } }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Tenure (years)"
                      type="number"
                      size="small"
                      error={!!errors.tenure}
                      helperText={errors.tenure?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="interestRate"
                  control={control}
                  rules={{ required: 'Interest rate is required', min: { value: 0.1, message: 'Min 0.1%' }, max: { value: 25, message: 'Max 25%' } }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Interest Rate (%)"
                      type="number"
                      size="small"
                      error={!!errors.interestRate}
                      helperText={errors.interestRate?.message}
                      slotProps={{ htmlInput: { step: 0.1 } }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Check Eligibility'
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      {/* Results Panel */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Results
          </Typography>
          <Divider sx={{ my: 2 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!result && !error && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Fill the form and click "Check Eligibility" to see results.
              </Typography>
            </Box>
          )}

          {result && (
            <Box>
              {/* Verdict */}
              <Box
                sx={{
                  textAlign: 'center',
                  mb: 3,
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: result.eligible ? 'success.light' : 'error.light',
                }}
              >
                {result.eligible ? (
                  <CheckCircleOutlinedIcon sx={{ fontSize: 48, color: 'success.dark' }} />
                ) : (
                  <CancelOutlinedIcon sx={{ fontSize: 48, color: 'error.dark' }} />
                )}
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    color: result.eligible ? 'success.dark' : 'error.dark',
                    mt: 1,
                  }}
                >
                  {result.eligible ? 'Eligible' : 'Not Eligible'}
                </Typography>
              </Box>

              {/* Details */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Eligible Loan Amount
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatCurrency(result.eligibleAmount)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Estimated Monthly EMI
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatCurrency(result.estimatedEMI)}
                  </Typography>
                </Box>

                {/* Suggested Banks */}
                {result.suggestedBanks && result.suggestedBanks.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Suggested Banks
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {result.suggestedBanks.map((bank, idx) => (
                        <Chip
                          key={idx}
                          label={typeof bank === 'string' ? bank : bank.name || bank.bankName}
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EligibilityCalculator;
